import React, { Fragment } from 'react';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import {
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { SortableChecklistItem } from './SortableChecklistItem';
import { AddBetweenButton } from './AddBetweenButton';
import { ChecklistEmptyState } from './ChecklistEmptyState';
import type { ChecklistItem } from '../types';

type ChecklistContentProps = {
  items: ChecklistItem[];
  isSaving: boolean;
  sideFormData: {
    mode: 'add' | 'edit';
    item?: ChecklistItem;
    position?: number;
  } | null;
  onEdit: (item: ChecklistItem) => void;
  onDelete: (itemId: string) => Promise<void>;
  onAddAtPosition: (position: number) => void;
  onAddNew: () => void;
};

export const ChecklistContent: React.FC<ChecklistContentProps> = ({
  items,
  isSaving,
  sideFormData,
  onEdit,
  onDelete,
  onAddAtPosition,
  onAddNew
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (items.length === 0) {
    return <ChecklistEmptyState onAddItem={onAddNew} />;
  }

  return (
    <SortableContext
      items={items.map(item => item.id)}
      strategy={verticalListSortingStrategy}
    >
      <Stack spacing={0} sx={{ pb: 8 }}>
        {items.length > 0 && !isMobile && (
          <AddBetweenButton
            onClick={() => onAddAtPosition(0)}
            disabled={isSaving}
          />
        )}

        {items.map((item, index) => (
          <Fragment key={item.id}>
            <SortableChecklistItem
              item={item}
              onEdit={onEdit}
              onDelete={onDelete}
              disabled={isSaving}
              isEditing={
                sideFormData?.mode === 'edit' &&
                sideFormData?.item?.id === item.id
              }
            />

            {!isMobile && (
              <AddBetweenButton
                onClick={() => onAddAtPosition(index + 1)}
                disabled={isSaving}
              />
            )}
          </Fragment>
        ))}
      </Stack>
    </SortableContext>
  );
};
